import { PathRequestPartStatusEnum, PathRequestPartTypeEnum } from '@elearning-platform/api-types/enums';
import { PathPartInfoInterface, PathRequestExamInterface, UserPathRequestDetailsInterface } from '@elearning-platform/api-types/interfaces';
import { getPathWithParams, IconName, TasksListItem, useFormatDate, UseFormatDate } from '@elearning-platform/ui';
import { TransProps, useTranslation } from 'react-i18next';

import { RouteNameEnum } from '../enums';
import { UseUserPathRequestToDoLists } from '../interfaces';
import { getUrlByRouteName, isPartInStatuses } from '../utils';

export const useUserPathRequestToDoLists: () => UseUserPathRequestToDoLists = (): UseUserPathRequestToDoLists => {
  const { t }: TransProps<never> = useTranslation();
  const { formatDate }: UseFormatDate = useFormatDate();

  const getUserPathRequestToDoListForUser: (
    pathRequest: UserPathRequestDetailsInterface
  ) => TasksListItem[] = (
    pathRequest: UserPathRequestDetailsInterface
  ): TasksListItem[] => {
    const list: string[] = [];

    // TODO: move to global const
    const inProgressStatuses: PathRequestPartStatusEnum[] = [
      PathRequestPartStatusEnum.Open,
      PathRequestPartStatusEnum.InProgress,
      PathRequestPartStatusEnum.InRetakeProgress,
    ];
    const waitingForReviewStatuses: PathRequestPartStatusEnum[] = [
      PathRequestPartStatusEnum.WaitingForReview,
      PathRequestPartStatusEnum.WaitingForRetakeReview,
    ];
    const inReviewStatuses: PathRequestPartStatusEnum[] = [
      PathRequestPartStatusEnum.InReview,
      PathRequestPartStatusEnum.InRetakeReview,
    ];
    const rejectedStatuses: PathRequestPartStatusEnum[] = [
      PathRequestPartStatusEnum.Rejected,
      PathRequestPartStatusEnum.RejectedInRetake,
    ];

    if (isPartInStatuses(inProgressStatuses, pathRequest.parts, PathRequestPartTypeEnum.Preform)) {
      list.push(t('toyota.app.useUserPathRequestToDoLists.userToDo.inProgressStatuses.preform'));
    }
    if (
      isPartInStatuses(waitingForReviewStatuses, pathRequest.parts, PathRequestPartTypeEnum.Preform)
      || isPartInStatuses(waitingForReviewStatuses, pathRequest.parts, PathRequestPartTypeEnum.DataForm)
      || isPartInStatuses(waitingForReviewStatuses, pathRequest.parts, PathRequestPartTypeEnum.FileUploadForm)
    ) {
      list.push(t('toyota.app.useUserPathRequestToDoLists.userToDo.waitingForReviewStatuses.forms'));
    }
    if (
      isPartInStatuses(inReviewStatuses, pathRequest.parts, PathRequestPartTypeEnum.Preform)
      || isPartInStatuses(inReviewStatuses, pathRequest.parts, PathRequestPartTypeEnum.DataForm)
      || isPartInStatuses(inReviewStatuses, pathRequest.parts, PathRequestPartTypeEnum.FileUploadForm)
    ) {
      list.push(t('toyota.app.useUserPathRequestToDoLists.userToDo.inReviewStatuses.forms'));
    }
    if (
      isPartInStatuses(rejectedStatuses, pathRequest.parts, PathRequestPartTypeEnum.Preform)
      || isPartInStatuses(rejectedStatuses, pathRequest.parts, PathRequestPartTypeEnum.DataForm)
      || isPartInStatuses(rejectedStatuses, pathRequest.parts, PathRequestPartTypeEnum.FileUploadForm)
    ) {
      list.push(t('toyota.app.useUserPathRequestToDoLists.userToDo.rejectedStatuses.forms'));
    }
    if (isPartInStatuses(inProgressStatuses, pathRequest.parts, PathRequestPartTypeEnum.CourseMaterials)) {
      list.push(t('toyota.app.useUserPathRequestToDoLists.userToDo.inProgressStatuses.courseMaterials'));
    }
    if (isPartInStatuses(inProgressStatuses, pathRequest.parts, PathRequestPartTypeEnum.DataForm)) {
      list.push(t('toyota.app.useUserPathRequestToDoLists.userToDo.inProgressStatuses.dataForm'));
    }
    if (isPartInStatuses(inProgressStatuses, pathRequest.parts, PathRequestPartTypeEnum.FileDownload)) {
      list.push(t('toyota.app.useUserPathRequestToDoLists.userToDo.inProgressStatuses.fileDownload'));
    }
    if (isPartInStatuses(inProgressStatuses, pathRequest.parts, PathRequestPartTypeEnum.FileUploadForm)) {
      list.push(t('toyota.app.useUserPathRequestToDoLists.userToDo.inProgressStatuses.fileUploadForm'));
    }
    if (isPartInStatuses(inProgressStatuses, pathRequest.parts, PathRequestPartTypeEnum.PaperDocumentFlow)) {
      list.push(t(
        'toyota.app.useUserPathRequestToDoLists.userToDo.inProgressStatuses.paperDocumentFlow',
        {
          path: getPathWithParams(
            getUrlByRouteName(RouteNameEnum.UserPathRequestPaperDocumentsList)?.url ?? '',
            { id: pathRequest.id.toString() }
          ),
        }
      ));
    }
    if (isPartInStatuses(inProgressStatuses, pathRequest.parts, PathRequestPartTypeEnum.ExamRegistration)) {
      const isUserRegistered: boolean = !!pathRequest.exam?.event;
      list.push(isUserRegistered
        ? t('toyota.app.useUserPathRequestToDoLists.userToDo.inProgressStatuses.examRegistration.registered', {
          date: pathRequest.exam?.event ? formatDate(pathRequest.exam.event.eventDateFromS, 'DD.MM.YYYY') : '',
        })
        : t('toyota.app.useUserPathRequestToDoLists.userToDo.inProgressStatuses.examRegistration.notRegistered')
      );
      if (isUserRegistered) {
        list.push(t('toyota.app.useUserPathRequestToDoLists.userToDo.inProgressStatuses.examRegistration.waitForResult'));
      }
    }
    if (isPartInStatuses(rejectedStatuses, pathRequest.parts, PathRequestPartTypeEnum.PaperDocumentFlow)) {
      list.push(t('toyota.app.useUserPathRequestToDoLists.userToDo.rejectedStatuses.paperDocumentFlow'));
    }

    return list.map((text: string): TasksListItem => ({ text, icon: IconName.DocumentOutlined }));
  };

  const getUserPathRequestToDoListForAdmin: (
    parts: Partial<Record<PathRequestPartTypeEnum, PathPartInfoInterface>>, examData: PathRequestExamInterface | null, isReadonly?: boolean
  ) => TasksListItem[] = (
    parts: Partial<Record<PathRequestPartTypeEnum, PathPartInfoInterface>>, examData: PathRequestExamInterface | null, isReadOnly?: boolean,
  ): TasksListItem[] => {
    const list: string[] = [];

    // TODO: move to global const
    const inProgressStatuses: PathRequestPartStatusEnum[] = [
      PathRequestPartStatusEnum.Open,
      PathRequestPartStatusEnum.InProgress,
      PathRequestPartStatusEnum.InRetakeProgress,
    ];
    const reviewStatuses: PathRequestPartStatusEnum[] = [
      PathRequestPartStatusEnum.WaitingForReview,
      PathRequestPartStatusEnum.WaitingForRetakeReview,
      PathRequestPartStatusEnum.InReview,
      PathRequestPartStatusEnum.InRetakeReview,
    ];
    const rejectedStatuses: PathRequestPartStatusEnum[] = [
      PathRequestPartStatusEnum.Rejected,
      PathRequestPartStatusEnum.RejectedInRetake,
    ];

    const readOnlySuffix: string = isReadOnly ? 'ReadOnly' : '';

    if (isPartInStatuses(inProgressStatuses, parts, PathRequestPartTypeEnum.Preform)) {
      list.push(t('toyota.app.useUserPathRequestToDoLists.adminToDo.inProgressStatuses.preform'));
    }
    if (
      isPartInStatuses(reviewStatuses, parts, PathRequestPartTypeEnum.Preform)
      || isPartInStatuses(reviewStatuses, parts, PathRequestPartTypeEnum.DataForm)
      || isPartInStatuses(reviewStatuses, parts, PathRequestPartTypeEnum.FileUploadForm)
    ) {
      list.push(t(`toyota.app.useUserPathRequestToDoLists.adminToDo.reviewStatuses.forms${readOnlySuffix}`));
    }
    if (
      isPartInStatuses(rejectedStatuses, parts, PathRequestPartTypeEnum.Preform)
      || isPartInStatuses(rejectedStatuses, parts, PathRequestPartTypeEnum.DataForm)
      || isPartInStatuses(rejectedStatuses, parts, PathRequestPartTypeEnum.FileUploadForm)
    ) {
      list.push(t('toyota.app.useUserPathRequestToDoLists.adminToDo.rejectedStatuses.forms'));
    }
    if (isPartInStatuses(inProgressStatuses, parts, PathRequestPartTypeEnum.DataForm)) {
      list.push(t('toyota.app.useUserPathRequestToDoLists.adminToDo.inProgressStatuses.dataForm'));
    }
    if (isPartInStatuses(inProgressStatuses, parts, PathRequestPartTypeEnum.FileUploadForm)) {
      list.push(t('toyota.app.useUserPathRequestToDoLists.adminToDo.inProgressStatuses.fileUploadForm'));
    }
    if (isPartInStatuses(inProgressStatuses, parts, PathRequestPartTypeEnum.ExamRegistration)) {
      const isUserRegistered: boolean = !!examData?.event;
      list.push(isUserRegistered
        ? t(
          'toyota.app.useUserPathRequestToDoLists.adminToDo.inProgressStatuses.examRegistration.registered',
          { examName: examData?.name }
        )
        : t(`toyota.app.useUserPathRequestToDoLists.adminToDo.inProgressStatuses.examRegistration.notRegistered${readOnlySuffix}`)
      );
    }
    if (isPartInStatuses(inProgressStatuses, parts, PathRequestPartTypeEnum.PaperDocumentFlow)) {
      list.push(t(`toyota.app.useUserPathRequestToDoLists.adminToDo.inProgressStatuses.paperDocumentFlow${readOnlySuffix}`));
    }
    if (isPartInStatuses(reviewStatuses, parts, PathRequestPartTypeEnum.PaperDocumentFlow)) {
      list.push(t(`toyota.app.useUserPathRequestToDoLists.adminToDo.reviewStatuses.paperDocumentFlow${readOnlySuffix}`));
    }
    if (isPartInStatuses(inProgressStatuses, parts, PathRequestPartTypeEnum.ExamResult)) {
      list.push(t(`toyota.app.useUserPathRequestToDoLists.adminToDo.inProgressStatuses.examResult${readOnlySuffix}`));
    }
    if (isPartInStatuses(inProgressStatuses, parts, PathRequestPartTypeEnum.ExamTypeAssignment)) {
      list.push(t(`toyota.app.useUserPathRequestToDoLists.adminToDo.inProgressStatuses.examTypeAssignment${readOnlySuffix}`));
    }

    return list.map((text: string): TasksListItem => ({ text, icon: IconName.DocumentOutlined }));
  };

  return { getUserPathRequestToDoListForUser, getUserPathRequestToDoListForAdmin };
};
